import React, { useCallback } from "react";
import { Box, Stack, TextField, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import SearchBar from "./SearchBar";
import FilterDropdown from "./FilterDropdown";
import SearchButton from "./SearchButton";
import { useTranslation } from "react-i18next";

export default function SearchFilterBar(props) {
    const [t] = useTranslation();
  //#region Props
  const {
    // -------------------------------------------------------------------------
    // Behavior
    // -------------------------------------------------------------------------
    mode = "backend", // "backend" | "frontend"

    // -------------------------------------------------------------------------
    // Container / Layout
    // -------------------------------------------------------------------------
    containerSx = {},
    stackSx = {},
    spacing = 2,

    // -------------------------------------------------------------------------
    // Search
    // -------------------------------------------------------------------------
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search",
    searchBarProps = {},
    searchBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Filter Dropdown (existing)
    // -------------------------------------------------------------------------
    showFilterButton = true,
    filterValue,
    onFilterChange,
    filterOptions = [],
    filterLabel = "Filter",
    filterPlaceholder = "All",
    filterProps = {},
    filterBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Search Button (existing)
    // -------------------------------------------------------------------------
    showSearchButton = true,
    onSearch,
    searchButtonText = "Search",
    searchButtonProps = {},
    searchButtonBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Date Range
    // -------------------------------------------------------------------------
    showDateRange = false,
    startDateLabel = "Start date",
    endDateLabel = "End date",
    startDateValue = "",
    endDateValue = "",
    onStartDateChange,
    onEndDateChange,
    dateInputSx = {},
    dateBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Actions (Apply / Clear)
    // -------------------------------------------------------------------------
    showActions = false,
    applyText = "Apply",
    clearText = "Clear",
    onApply,
    onClear,
    applyButtonProps = {},
    clearButtonProps = {},
    actionsBoxSx = {},
  } = props;
  //#endregion

  //#region Handlers

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;

      if (mode === "backend") {
        if (showActions && typeof onApply === "function") return onApply();
        if (typeof onSearch === "function") return onSearch();
        return;
      }

      // frontend mode: optional; if callbacks exist, run them
      if (showActions && typeof onApply === "function") return onApply();
      if (typeof onSearch === "function") return onSearch();
    },
    [mode, showActions, onApply, onSearch]
  );
  //#endregion

  //#region Render Helpers
  const renderSearch = () => (
    <Box sx={{ flex: 1, width: { xs: "100%", md: "auto" }, minWidth: { md: 260 }, ...searchBoxSx }}>
      <SearchBar
        value={searchValue}
        onChange={onSearchChange}
        placeholder={t(searchPlaceholder)}
        onKeyDown={handleKeyDown}
        fullWidth
        {...searchBarProps}
      />
    </Box>
  );

  const renderFilter = () => {
    if (!showFilterButton) return null;

    return (
      <Box sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 200 }, ...filterBoxSx }}>
        <FilterDropdown
          value={filterValue}
          onChange={onFilterChange}
          options={filterOptions}
          label={t(filterLabel)}
          placeholder={t(filterPlaceholder)}
          fullWidth
          {...filterProps}
        />
      </Box>
    );
  };

  const renderDateRange = () => {
    if (!showDateRange) return null;

    return (
      <>
        <Box sx={{ width: { xs: "100%", md: "auto" }, ...dateBoxSx }}>
          <TextField
            label={t(startDateLabel)}
            type="date"
            size="small"
            value={startDateValue}
            onChange={(e) => onStartDateChange?.(e.target.value)}
            InputLabelProps={{ shrink: true }}
            onKeyDown={handleKeyDown}
            fullWidth
            sx={{ width: { xs: "100%", md: "auto" }, ...dateInputSx }}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", md: "auto" }, ...dateBoxSx }}>
          <TextField
            label={t(endDateLabel)}
            type="date"
            size="small"
            value={endDateValue}
            onChange={(e) => onEndDateChange?.(e.target.value)}
            InputLabelProps={{ shrink: true }}
            onKeyDown={handleKeyDown}
            fullWidth
            sx={{ width: { xs: "100%", md: "auto" }, ...dateInputSx }}
          />
        </Box>
      </>
    );
  };

  const renderSearchButton = () => {
    if (!showSearchButton) return null;

    return (
      <Box sx={{ width: { xs: "100%", md: "auto" }, ...searchButtonBoxSx }}>
        <SearchButton 
          onClick={onSearch} 
          text={searchButtonText} 
          fullWidth 
          {...searchButtonProps} 
        />
      </Box>
    );
  };

  const renderActions = () => {
    if (!showActions) return null;

    return (
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, width: { xs: "100%", md: "auto" }, alignItems: { xs: "stretch", md: "center" }, gap: 1, ...actionsBoxSx }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onApply}
          startIcon={<SearchIcon fontSize="small" />}
          fullWidth
          {...applyButtonProps}
        >
          {t(applyText)}
        </Button>

        <Button
          variant="text"
          color="secondary"
          onClick={onClear}
          startIcon={<ClearIcon fontSize="small" />}
          fullWidth
          {...clearButtonProps}
        >
          {t(clearText)}
        </Button>
      </Box>
    );
  };
  //#endregion

  //#region Output
  return (
    <Box sx={containerSx}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={spacing}
        alignItems={{ xs: "stretch", md: "center" }}
        useFlexGap
        flexWrap="wrap"
        sx={{ width: "100%", ...stackSx }}
      >
        {renderSearch()}
        {renderFilter()}
        {renderDateRange()}
        {renderSearchButton()}
        {renderActions()}
      </Stack>
    </Box>
  );
  //#endregion
}
